import { useRef, ReactNode, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme, useMediaQuery } from '@mui/material';
import Drawer, { drawerClasses } from '@mui/material/Drawer';

import { paper } from 'src/theme/css';
import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';

import { RecordPanelMode } from './record-panel.types';

// ----------------------------------------------------------------------

interface RecordPanelFrameProps {
  /** `quick` = right-side drawer (eye); `overlay` = centred, dimmed modal (name). */
  variant: RecordPanelMode;
  open: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  /** 1-based position within the result set; hidden when absent (e.g. a cold deep-link). */
  position?: { index: number; total: number };
  children: ReactNode;
}

/**
 * The record-panel chrome (JT1-1529). Two presentations of the same content, chosen by `variant`:
 * the quick-view drawer slides in from the right and leaves the list bright behind it (spec D); the
 * overlay is a centred modal that dims the list (spec E). Both carry prev/next navigation, a
 * "Result N of M" indicator and close, and both preserve the list state (the URL param, owned by the
 * caller, is what makes them linkable). Keyboard ← / → step the result set; Esc closes.
 */
export default function RecordPanelFrame({ variant, open, onClose, onPrev, onNext, position, children }: RecordPanelFrameProps) {
  const theme = useTheme();
  const { t } = useTranslate();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const contentRef = useRef<HTMLDivElement>(null);

  // Overlay mode renders the full detail views, which normally get gutter padding from the dashboard
  // layout; inside the modal they'd sit flush to the edges, so pad the content. The quick-view drawer
  // uses compact views that already self-pad, so it stays at the tight chrome padding. (JT1-1529 QA)
  const isOverlay = variant === 'overlay';

  // Keyboard navigation: arrows walk the result set, Esc closes. Don't hijack arrows while typing.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      // A nested modal opened on top of the panel (e.g. Send for signing) marks our container
      // aria-hidden via MUI's ModalManager. Don't steal its arrow keys — that would swap the record,
      // and the open modal's target, out from under the user (JT1-1529 review).
      if (contentRef.current?.closest('[aria-hidden="true"]')) return;
      const target = e.target as HTMLElement;
      const typing = target?.closest('input, textarea, [contenteditable="true"]');
      if (typing) return;
      if (e.key === 'ArrowLeft' && onPrev) { e.preventDefault(); onPrev(); }
      else if (e.key === 'ArrowRight' && onNext) { e.preventDefault(); onNext(); }
      else if (e.key === 'Escape') { onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onPrev, onNext, onClose]);

  const chrome = (
    <Stack ref={contentRef} sx={{ height: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: isOverlay ? { xs: 2, sm: 3 } : 1,
          py: 1,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          borderBottom: (themex) => `1px solid ${themex.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={t('common.previous', { defaultValue: 'Previous' })}>
            <span>
              <IconButton size="small" onClick={onPrev} disabled={!onPrev}>
                <Iconify icon="mingcute:arrow-left-fill" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('common.next', { defaultValue: 'Next' })}>
            <span>
              <IconButton size="small" onClick={onNext} disabled={!onNext}>
                <Iconify icon="mingcute:arrow-right-fill" />
              </IconButton>
            </span>
          </Tooltip>
          {position && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {t('record_panel.position', {
                defaultValue: 'Result {{index}} of {{total}}',
                index: position.index,
                total: position.total,
              })}
            </Typography>
          )}
        </Stack>

        <IconButton size="small" onClick={onClose}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Stack>

      <Stack
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          ...(isOverlay && { px: { xs: 2, sm: 3 }, pt: 2, pb: 3 }),
        }}
      >
        {children}
      </Stack>
    </Stack>
  );

  if (variant === 'overlay') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '90vh',
            ...paper({ theme, bgcolor: theme.palette.background.default }),
          },
        }}
      >
        {chrome}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { invisible: true } }}
      PaperProps={{ sx: { width: isMobile ? '100%' : 640 } }}
      sx={{ [`& .${drawerClasses.paper}`]: { ...paper({ theme, bgcolor: theme.palette.background.default }) } }}
    >
      {chrome}
    </Drawer>
  );
}
